import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSlug from 'remark-slug';
import rehypeHighlight from 'rehype-highlight';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import { cn } from '@/lib/utils';

interface MarkdownComponentProps {
  className?: string;
}

const markdownComponents = {
  // Headings
  h1: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1
      className={cn('scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl', className)}
      {...props}
    />
  ),
  h2: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2
      className={cn(
        'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
        className
      )}
      {...props}
    />
  ),
  h3: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className={cn('scroll-m-20 text-2xl font-semibold tracking-tight', className)} {...props} />
  ),
  h4: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4 className={cn('scroll-m-20 text-xl font-semibold tracking-tight', className)} {...props} />
  ),

  // Paragraphs and text
  p: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className={cn('leading-7 [&:not(:first-child)]:mt-6', className)} {...props} />
  ),

  // Lists
  ul: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLUListElement>) => (
    <ul className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)} {...props} />
  ),
  ol: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLOListElement>) => (
    <ol className={cn('my-6 ml-6 list-decimal [&>li]:mt-2', className)} {...props} />
  ),
  li: ({ className, ...props }: MarkdownComponentProps & React.HTMLAttributes<HTMLLIElement>) => (
    <li className={cn('mt-2', className)} {...props} />
  ),

  // Blockquotes
  blockquote: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className={cn('mt-6 border-l-2 pl-6 italic', className)} {...props} />
  ),

  // Tables
  table: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-6 w-full overflow-y-auto">
      <table
        className={cn('w-full border-collapse border-2 border-border bg-background', className)}
        {...props}
      />
    </div>
  ),
  tr: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr
      className={cn('border-b border-border hover:bg-muted/50 transition-colors', className)}
      {...props}
    />
  ),
  th: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLTableCellElement>) => (
    <th
      className={cn(
        'border border-border px-4 py-3 text-left font-semibold bg-muted text-foreground',
        '[&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),
  td: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLTableCellElement>) => (
    <td
      className={cn(
        'border border-border px-4 py-3 text-left bg-background',
        '[&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),

  // Code
  pre: ({ className, ...props }: MarkdownComponentProps & React.HTMLAttributes<HTMLPreElement>) => (
    <pre
      className={cn(
        'mb-4 mt-6 overflow-x-auto rounded-lg border bg-zinc-950 py-4 dark:bg-zinc-900',
        className
      )}
      {...props}
    />
  ),
  code: ({
    className,
    inline,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLElement> & { inline?: boolean }) => {
    if (inline) {
      return (
        <code
          className={cn(
            'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
            className
          )}
          {...props}
        />
      );
    }
    return (
      <code
        className={cn('relative rounded font-mono text-sm font-semibold', className)}
        {...props}
      />
    );
  },

  // Links
  a: ({
    className,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLAnchorElement>) => (
    <a className={cn('font-medium underline underline-offset-4', className)} {...props} />
  ),

  // Images
  img: ({
    className,
    alt,
    ...props
  }: MarkdownComponentProps & React.HTMLAttributes<HTMLImageElement>) => (
    <img className={cn('rounded-md border', className)} alt={alt} {...props} />
  ),

  // Horizontal rule
  hr: ({ ...props }) => <hr className="my-4 md:my-8" {...props} />,

  // Strong and emphasis
  strong: ({ className, ...props }: MarkdownComponentProps & React.HTMLAttributes<HTMLElement>) => (
    <strong className={cn('font-semibold', className)} {...props} />
  ),
  em: ({ className, ...props }: MarkdownComponentProps & React.HTMLAttributes<HTMLElement>) => (
    <em className={cn('italic', className)} {...props} />
  ),
};

interface MarkdownProps {
  content: string;
  className?: string;
}

export function Markdown({ content, className }: MarkdownProps) {
  return (
    <div className={cn('prose prose-gray dark:prose-invert max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkSlug]}
        rehypePlugins={[rehypeHighlight, [rehypeAutolinkHeadings, { behavior: 'wrap' }]]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
